/**
 * A project's folder on this Mac (RFC 0337 3i), from the desktop daemon.
 *
 * The window cannot know a path or open a folder picker, so it asks the
 * daemon, which asks the shell. Outside Mod Desktop there is no daemon, and
 * `readProjectFolder` answers null, which hides the menu item.
 */

import { useEffect, useState } from 'react'
import { localServer } from '../../sdk/localServer'

export interface ProjectFolder {
  /** Null when the project has no folder on this Mac. */
  path: string | null
  git: boolean
}

async function post(path: string, body: unknown): Promise<unknown> {
  const res = await fetch(`${localServer()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => null)) as {
    error?: unknown
  } | null
  if (!res.ok) {
    throw new Error(
      typeof data?.error === 'string'
        ? data.error
        : `The folder could not change (${res.status})`,
    )
  }
  return data
}

/**
 * A daemon that cannot be reached is not a daemon that said no: a mount or a
 * move restarts its servers for a few seconds, and a fetch then fails outright
 * (a TypeError), where a refusal is an HTTP answer. Wait and ask again.
 */
async function postRetrying(path: string, body: unknown): Promise<unknown> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await post(path, body)
    } catch (error) {
      if (!(error instanceof TypeError) || attempt >= 6) throw error
      await new Promise((r) => setTimeout(r, 500 * 2 ** attempt))
    }
  }
}

/** Where the project lives here, or null with no desktop daemon. */
export async function readProjectFolder(
  projectId: string,
  sourceId?: string,
): Promise<ProjectFolder | null> {
  try {
    const data = (await postRetrying('/folder', {
      projectId,
      sourceId,
    })) as ProjectFolder
    return { path: data.path ?? null, git: data.git === true }
  } catch {
    return null
  }
}

/**
 * Ask for a new folder. Resolves to the path chosen, or null when the person
 * cancelled; the daemon restarts on it a moment later. Throws with the
 * daemon's sentence when it refuses, as it does while a turn is running.
 */
export async function chooseProjectFolder(
  projectId: string,
  force = false,
  sourceId?: string,
): Promise<string | null> {
  return (await chooseSourceFolder(projectId, sourceId, force))?.path ?? null
}

/** Pick and mount a source on this device, reporting its probed kind. */
export async function chooseSourceFolder(
  projectId: string,
  sourceId?: string,
  force = false,
): Promise<{
  path: string
  git: boolean
  code?: {
    trunk: string
    headCommit: string
    remote?: string
  } | null
} | null> {
  const data = (await post('/folder/choose', {
    projectId,
    sourceId,
    ...(force ? { force: true } : {}),
  })) as {
    path?: unknown
    git?: unknown
    code?: {
      trunk: string
      headCommit: string
      remote?: string
    } | null
  }
  const chosen = typeof data.path === 'string' ? data.path : null
  // The daemon restarts on the new folder; readers ask again once it is up.
  if (chosen && typeof window !== 'undefined')
    setTimeout(
      () => window.dispatchEvent(new Event(PROJECT_FOLDER_EVENT)),
      5000,
    )
  return chosen
    ? { path: chosen, git: data.git === true, code: data.code }
    : null
}

export async function unmountProjectSource(
  projectId: string,
  sourceId: string,
): Promise<void> {
  await post('/source/unmount', { projectId, sourceId })
  if (typeof window !== 'undefined')
    window.dispatchEvent(new Event(PROJECT_FOLDER_EVENT))
}

/** The project's folder path on this Mac, or null. Used only to name files
 *  inside it by their place in the project. */
export function useProjectFolderPath(
  projectId: string | undefined,
  sourceId?: string,
): string | null {
  const [folder, setFolder] = useState<string | null>(null)
  useEffect(() => {
    if (!projectId) return
    let live = true
    void readProjectFolder(projectId, sourceId).then((f) => {
      if (live) setFolder(f?.path ?? null)
    })
    return () => {
      live = false
    }
  }, [projectId, sourceId])
  return folder
}

/** Fired after a folder changes, so readers ask again. */
export const PROJECT_FOLDER_EVENT = 'mod:project-folder'

/**
 * The projects whose folder on this Mac is a git checkout (RFC 0337 3e): they
 * have files to show whether or not the project document ever adopted a
 * repository. Empty outside Mod Desktop.
 */
export function useGitFolders(projectIds: readonly string[]): Set<string> {
  const [git, setGit] = useState<Set<string>>(() => new Set())
  const key = projectIds.join(',')
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    const again = () => setAsked((n) => n + 1)
    window.addEventListener(PROJECT_FOLDER_EVENT, again)
    return () => window.removeEventListener(PROJECT_FOLDER_EVENT, again)
  }, [])
  // `key` stands for the ids; `asked` re-reads after a change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed
  useEffect(() => {
    let live = true
    const ids = key ? key.split(',') : []
    void Promise.all(
      ids.map(async (id) => ((await readProjectFolder(id))?.git ? id : null)),
    ).then((found) => {
      if (live) setGit(new Set(found.filter((id): id is string => !!id)))
    })
    return () => {
      live = false
    }
  }, [key, asked])
  return git
}
